import { useTheme } from '@mui/material/styles';
import { OPTIONS, FONTS } from '../../../../config/app-config';
import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, TextField, Checkbox, Button, Chip,
    Autocomplete, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import ManageReleaseFilesSection from './ManageReleaseFilesSection';

const productOptions = OPTIONS.products;
const languageOptions = OPTIONS.languages;

export default function EditReleaseDialog({
    open,
    onClose,
    formData,
    onChange,
    onTogglePublic,
    onProductsChange,
    onLanguagesChange,
    onSave,              // (meta, files) => void
    isEditing,
    saving = false,
}) {
    const theme = useTheme();
    /* Upload-Grid-State */
    const [releaseFiles, setReleaseFiles] = useState({});

    useEffect(() => {
        if (!open) {
            setReleaseFiles({});
            return;
        }

        setReleaseFiles((prev) => {
            const next = {};

            formData.products.forEach((product) => {
                const prevProductState = prev[product];
                if (!prevProductState) return;

                const nextUploads = {};
                formData.languages.forEach((language) => {
                    if (Object.prototype.hasOwnProperty.call(prevProductState.uploads || {}, language)) {
                        nextUploads[language] = prevProductState.uploads[language];
                    }
                });

                next[product] = {
                    ...prevProductState,
                    uploads: nextUploads,
                };
            });

            return next;
        });
    }, [open, formData.products, formData.languages]);

    const handleSaveClick = () => {
        onSave({ ...formData }, releaseFiles);  // Progress übernimmt Parent
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen >
            {/* ---------- Header ---------- */}
            <DialogTitle disableTypography sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: 'common.white' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" noWrap sx={{ fontFamily: FONTS.heading, fontWeight: 700 }}>
                        {isEditing ? 'Edit Release' : 'New Release'}
                    </Typography>
                    <IconButton edge="end" color="inherit" onClick={onClose} disabled={saving}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* ---------- Formular ---------- */}
            <DialogContent>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Version Code (e.g. 1400)"
                        name="code"
                        value={formData.code}
                        onChange={onChange}
                        fullWidth
                        sx={{ mt: 2 }}
                    />

                    <TextField
                        label="Display Name"
                        name="displayName"
                        value={formData.displayName}
                        onChange={onChange}
                        fullWidth
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox checked={formData.public} onChange={onTogglePublic} />
                        <Typography>Show to all Users (Public)</Typography>
                    </Box>

                    {/* Produkte */}
                    <Autocomplete
                        multiple
                        options={productOptions}
                        value={formData.products}
                        onChange={(_, v) => onProductsChange(v)}
                        renderTags={(value, getTagProps) =>
                            value.map((opt, i) => (
                                <Chip {...getTagProps({ index: i })} key={opt} label={opt} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} label="Available Products" />}
                    />

                    {/* Sprachen */}
                    <Autocomplete
                        multiple
                        options={languageOptions}
                        value={formData.languages}
                        onChange={(_, v) => onLanguagesChange(v)}
                        renderTags={(value, getTagProps) =>
                            value.map((opt, i) => (
                                <Chip {...getTagProps({ index: i })} key={opt} label={opt} sx={{ fontWeight: 500 }} />
                            ))
                        }
                        renderInput={(params) => <TextField {...params} label="Available Languages" />}
                    />
                </Box>
            </DialogContent>

            {/* Upload-Grid */}
            <ManageReleaseFilesSection
                products={formData.products}
                languages={formData.languages}
                value={releaseFiles}
                onChange={setReleaseFiles}
            />

            {/* ---------- Footer ---------- */}
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="error" disabled={saving}>Abort</Button>
                <Button
                    variant="contained"
                    onClick={handleSaveClick}
                    disabled={saving}
                    sx={{ backgroundColor: theme.palette.primary.main, color: '#fff' }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

